import {useMemo} from 'react';

import {
    useConsumerSubscriptionGwGPlan,
    useConsumerSubscriptionGwGPlusPersonalPlan,
    useConsumerSubscriptionPlan,
    type ConsumerSubscriptionGwGPersonalPlan,
    type ConsumerSubscriptionGwGStandalonePlan,
    type SubscriptionPlanContext,
    type SubscriptionPlanPriceOptionDiscount,
} from '@udemy/subscription-graphql';

export interface UseGwgSubscriptionPriceReturn {
    formattedMonthlyAmount: string | null;
    discount?: SubscriptionPlanPriceOptionDiscount;
    isLoading: boolean;
    plan: SubscriptionPlanContext<
        ConsumerSubscriptionGwGStandalonePlan | ConsumerSubscriptionGwGPersonalPlan
    > | null;
    isSubscriber: boolean;
}

/**
 * Custom hook to get GWG subscription pricing with automatic plan selection.
 *
 * Logic:
 * - If gwgPlan is not available OR user is existing Personal Plan subscriber
 *   → use combinedGwgPlan (GWG + Personal Plan)
 * - Otherwise → use gwgPlan (base GWG plan)
 *
 * @returns {UseGwgSubscriptionPriceReturn} Pricing data with loading state
 */
export function useGwgSubscriptionPrice(): UseGwgSubscriptionPriceReturn {
    const currentlySubscribedPlan = useConsumerSubscriptionPlan();
    const gwgPlan = useConsumerSubscriptionGwGPlan();
    const combinedGwgPlan = useConsumerSubscriptionGwGPlusPersonalPlan();

    return useMemo(() => {
        // Check if user is already a Personal Plan subscriber
        const isPersonalPlanSubscriber =
            currentlySubscribedPlan &&
            'isSubscriber' in currentlySubscribedPlan &&
            currentlySubscribedPlan.isSubscriber &&
            'isSubscriberToSpecificPlan' in currentlySubscribedPlan &&
            currentlySubscribedPlan.isSubscriberToSpecificPlan;

        // Determine which plan to use
        const selectedPlan = !gwgPlan || isPersonalPlanSubscriber ? combinedGwgPlan : gwgPlan;

        // Handle loading state
        if (!selectedPlan || selectedPlan.isLoading) {
            return {
                formattedMonthlyAmount: null,
                discount: undefined,
                isLoading: true,
                plan: null,
                isSubscriber: false,
            };
        }

        // Handle subscriber state (no pricing needed)
        if ('isSubscriber' in selectedPlan && selectedPlan.isSubscriber) {
            return {
                formattedMonthlyAmount: null,
                discount: undefined,
                isLoading: false,
                plan: selectedPlan,
                isSubscriber: true,
            };
        }

        // Extract pricing data from loaded plan
        if ('formattedMonthlyAmount' in selectedPlan) {
            return {
                formattedMonthlyAmount: selectedPlan.formattedMonthlyAmount,
                discount: selectedPlan.monthlyDiscount ?? selectedPlan.selectedDiscount,
                isLoading: false,
                plan: selectedPlan,
                isSubscriber: false,
            };
        }

        // Fallback for unexpected state
        return {
            formattedMonthlyAmount: null,
            discount: undefined,
            isLoading: false,
            plan: selectedPlan,
            isSubscriber: false,
        };
    }, [gwgPlan, combinedGwgPlan, currentlySubscribedPlan]);
}
